import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';
import Input from '../../../components/ui/Input';
import PaymentIntegration from './PaymentIntegration';

const HotelBookingForm = ({ hotel, onClose, onBack }) => {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(1);
  const [bookingData, setBookingData] = useState({
    guestInfo: {
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
      nationality: '',
      specialRequests: ''
    },
    bookingDetails: {
      checkInDate: '',
      checkOutDate: '',
      adults: 1,
      children: 0,
      roomType: '',
      numberOfRooms: 1
    },
    selectedRoom: null,
    paymentInfo: null
  });

  const steps = [
    { id: 1, title: 'Guest Information', icon: 'User' },
    { id: 2, title: 'Booking Details', icon: 'Calendar' },
    { id: 3, title: 'Room Selection', icon: 'Bed' },
    { id: 4, title: 'Payment', icon: 'CreditCard' },
    { id: 5, title: 'Confirmation', icon: 'CheckCircle' }
  ];

  const handleInputChange = (section, field, value) => {
    setBookingData(prev => ({
      ...prev,
      [section]: {
        ...prev[section],
        [field]: value
      }
    }));
  };

  const calculateNights = () => {
    if (!bookingData.bookingDetails.checkInDate || !bookingData.bookingDetails.checkOutDate) return 0;
    const checkIn = new Date(bookingData.bookingDetails.checkInDate);
    const checkOut = new Date(bookingData.bookingDetails.checkOutDate);
    const diffTime = Math.abs(checkOut - checkIn);
    return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  };

  const calculateTotal = () => {
    if (!bookingData.selectedRoom) return 0;
    const nights = calculateNights();
    const roomRate = bookingData.selectedRoom.price;
    const subtotal = roomRate * nights * bookingData.bookingDetails.numberOfRooms;
    const serviceFee = subtotal * 0.08; // 8% service fee
    const tax = subtotal * 0.18; // 18% VAT
    return {
      subtotal: Math.round(subtotal),
      serviceFee: Math.round(serviceFee),
      tax: Math.round(tax),
      total: Math.round(subtotal + serviceFee + tax)
    };
  };

  const validateStep = (step) => {
    switch (step) {
      case 1:
        const { firstName, lastName, email, phone } = bookingData.guestInfo;
        return firstName && lastName && email && phone;
      case 2:
        const { checkInDate, checkOutDate, adults } = bookingData.bookingDetails;
        return checkInDate && checkOutDate && adults > 0;
      case 3:
        return bookingData.selectedRoom !== null;
      default:
        return true;
    }
  };

  const handleNext = () => {
    if (validateStep(currentStep)) {
      setCurrentStep(prev => Math.min(prev + 1, steps.length));
    }
  };

  const handlePrev = () => {
    setCurrentStep(prev => Math.max(prev - 1, 1));
  };

  const handleRoomSelect = (room) => {
    setBookingData(prev => ({
      ...prev,
      selectedRoom: room,
      bookingDetails: {
        ...prev.bookingDetails,
        roomType: room.type
      }
    }));
  };


  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        return (
          <div className="space-y-6">
            <div>
              <h2 className="text-xl font-semibold mb-2">Guest Information</h2>
              <p className="text-muted-foreground">
                Please provide your personal details for the booking.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Input
                label="First Name *"
                type="text"
                value={bookingData.guestInfo.firstName}
                onChange={(e) => handleInputChange('guestInfo', 'firstName', e.target.value)}
                placeholder="Enter your first name"
                required
              />
              <Input
                label="Last Name *"
                type="text"
                value={bookingData.guestInfo.lastName}
                onChange={(e) => handleInputChange('guestInfo', 'lastName', e.target.value)}
                placeholder="Enter your last name"
                required
              />
              <Input
                label="Email Address *"
                type="email"
                value={bookingData.guestInfo.email}
                onChange={(e) => handleInputChange('guestInfo', 'email', e.target.value)}
                placeholder="your.email@example.com"
                required
              />
              <Input
                label="Phone Number *"
                type="tel"
                value={bookingData.guestInfo.phone}
                onChange={(e) => handleInputChange('guestInfo', 'phone', e.target.value)}
                placeholder="+256 700 000 000"
                required
              />
              <Input
                label="Nationality"
                type="text"
                value={bookingData.guestInfo.nationality}
                onChange={(e) => handleInputChange('guestInfo', 'nationality', e.target.value)}
                placeholder="e.g., Ugandan"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Special Requests
              </label>
              <textarea
                value={bookingData.guestInfo.specialRequests}
                onChange={(e) => handleInputChange('guestInfo', 'specialRequests', e.target.value)}
                rows={3}
                className="w-full px-3 py-2 border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent resize-none"
                placeholder="Any special requests or requirements..."
              />
            </div>
          </div>
        );

      case 2:
        return (
          <div className="space-y-6">
            <div>
              <h2 className="text-xl font-semibold mb-2">Booking Details</h2>
              <p className="text-muted-foreground">
                Select your check-in and check-out dates and number of guests.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Input
                label="Check-in Date *"
                type="date"
                value={bookingData.bookingDetails.checkInDate}
                onChange={(e) => handleInputChange('bookingDetails', 'checkInDate', e.target.value)}
                min={new Date().toISOString().split('T')[0]}
                required
              />
              <Input
                label="Check-out Date *"
                type="date"
                value={bookingData.bookingDetails.checkOutDate}
                onChange={(e) => handleInputChange('bookingDetails', 'checkOutDate', e.target.value)}
                min={bookingData.bookingDetails.checkInDate || new Date().toISOString().split('T')[0]}
                required
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Adults *
                </label>
                <select
                  value={bookingData.bookingDetails.adults}
                  onChange={(e) => handleInputChange('bookingDetails', 'adults', parseInt(e.target.value))}
                  className="w-full px-3 py-2 border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                >
                  {[1, 2, 3, 4, 5, 6].map(num => (
                    <option key={num} value={num}>{num} Adult{num > 1 ? 's' : ''}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Children
                </label>
                <select
                  value={bookingData.bookingDetails.children}
                  onChange={(e) => handleInputChange('bookingDetails', 'children', parseInt(e.target.value))}
                  className="w-full px-3 py-2 border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                >
                  {[0, 1, 2, 3, 4].map(num => (
                    <option key={num} value={num}>{num} Child{num !== 1 ? 'ren' : ''}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Number of Rooms
                </label>
                <select
                  value={bookingData.bookingDetails.numberOfRooms}
                  onChange={(e) => handleInputChange('bookingDetails', 'numberOfRooms', parseInt(e.target.value))}
                  className="w-full px-3 py-2 border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                >
                  {[1, 2, 3, 4, 5].map(num => (
                    <option key={num} value={num}>Room{num > 1 ? 's' : ''}</option>
                  ))}
                </select>
              </div>
            </div>

            {calculateNights() > 0 && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <div className="flex items-center">
                  <Icon name="Calendar" size={20} className="text-blue-500 mr-3" />
                  <div>
                    <p className="text-blue-800 font-medium">
                      {calculateNights()} night{calculateNights() > 1 ? 's' : ''} stay
                    </p>
                    <p className="text-blue-600 text-sm">
                      From {new Date(bookingData.bookingDetails.checkInDate).toLocaleDateString()} to {new Date(bookingData.bookingDetails.checkOutDate).toLocaleDateString()}
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>
        );

      case 3:
        return (
          <div className="space-y-6">
            <div>
              <h2 className="text-xl font-semibold mb-2">Room Selection</h2>
              <p className="text-muted-foreground">
                Choose your preferred room type for your stay.
              </p>
            </div>

            <div className="space-y-4">
              {hotel.rooms.map((room, index) => (
                <div
                  key={index}
                  className={`border rounded-lg p-4 cursor-pointer transition-all ${
                    bookingData.selectedRoom?.type === room.type
                      ? 'border-primary bg-primary/5'
                      : 'border-border hover:border-primary/50'
                  }`}
                  onClick={() => handleRoomSelect(room)}
                >
                  <div className="flex flex-col md:flex-row md:items-center justify-between mb-3">
                    <div>
                      <h3 className="text-lg font-semibold text-foreground mb-1">{room.type}</h3>
                      <div className="flex items-center gap-4 text-sm text-muted-foreground mb-2">
                        <span>{room.capacity} guests max</span>
                        <span>{room.size}</span>
                        <span>{room.bed}</span>
                      </div>
                      <div className="flex items-center text-sm text-muted-foreground">
                        <Icon name="Eye" size={14} className="mr-1" />
                        {room.view}
                      </div>
                    </div>
                    <div className="text-right mt-4 md:mt-0">
                      <div className="text-xl font-bold text-primary">
                        ${room.price}
                      </div>
                      <div className="text-sm text-muted-foreground">per night</div>
                      <div className={`text-sm font-medium ${room.available > 0 ? 'text-green-600' : 'text-red-600'}`}>
                        {room.available > 0 ? `${room.available} available` : 'Sold out'}
                      </div>
                    </div>
                  </div>

                  <div className="flex flex-wrap gap-2 mb-3">
                    {room.amenities.slice(0, 4).map((amenity, idx) => (
                      <span key={idx} className="px-2 py-1 bg-muted text-xs rounded-full">
                        {amenity}
                      </span>
                    ))}
                    {room.amenities.length > 4 && (
                      <span className="px-2 py-1 bg-muted text-xs rounded-full">
                        +{room.amenities.length - 4} more
                      </span>
                    )}
                  </div>

                  {bookingData.selectedRoom?.type === room.type && (
                    <div className="flex items-center text-primary">
                      <Icon name="Check" size={16} className="mr-2" />
                      <span className="text-sm font-medium">Selected</span>
                    </div>
                  )}
                </div>
              ))}
            </div>

            {bookingData.selectedRoom && calculateNights() > 0 && (
              <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-green-800 font-medium">Selected: {bookingData.selectedRoom.type}</p>
                    <p className="text-green-600 text-sm">
                      {calculateNights()} night{calculateNights() > 1 ? 's' : ''} × {bookingData.bookingDetails.numberOfRooms} room{bookingData.bookingDetails.numberOfRooms > 1 ? 's' : ''}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="text-green-800 font-bold">
                      ${bookingData.selectedRoom.price * calculateNights() * bookingData.bookingDetails.numberOfRooms}
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>
        );

      case 4:
        return (
          <PaymentIntegration
            bookingData={bookingData}
            hotel={hotel}
            onUpdate={(paymentResult) => {
              setBookingData(prev => ({
                ...prev,
                paymentInfo: paymentResult
              }));
            }}
            onNext={() => setCurrentStep(5)}
            onPrev={() => setCurrentStep(3)}
          />
        );

      case 5:
        return (
          <div className="space-y-6">
            <div className="text-center">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Icon name="CheckCircle" size={32} className="text-green-600" />
              </div>
              <h2 className="text-xl font-semibold mb-2">Booking Confirmed!</h2>
              <p className="text-muted-foreground">
                Your hotel reservation has been successfully processed.
              </p>
            </div>

            <div className="bg-card border border-border rounded-lg p-6">
              <h3 className="font-medium text-foreground mb-4">Booking Details</h3>
              <div className="space-y-3 text-sm">
                <div className="flex justify-between">
                  <span>Booking Reference:</span>
                  <span className="font-mono font-medium">HOTEL{Date.now().toString().slice(-8)}</span>
                </div>
                <div className="flex justify-between">
                  <span>Hotel:</span>
                  <span className="font-medium">{hotel.name}</span>
                </div>
                <div className="flex justify-between">
                  <span>Room Type:</span>
                  <span>{bookingData.selectedRoom?.type}</span>
                </div>
                <div className="flex justify-between">
                  <span>Check-in:</span>
                  <span>{new Date(bookingData.bookingDetails.checkInDate).toLocaleDateString()}</span>
                </div>
                <div className="flex justify-between">
                  <span>Check-out:</span>
                  <span>{new Date(bookingData.bookingDetails.checkOutDate).toLocaleDateString()}</span>
                </div>
                <div className="flex justify-between">
                  <span>Guests:</span>
                  <span>{bookingData.bookingDetails.adults + bookingData.bookingDetails.children}</span>
                </div>
                <div className="border-t border-border pt-3 mt-4">
                  <div className="flex justify-between font-medium">
                    <span>Total Paid:</span>
                    <span className="text-primary">${calculateTotal().total}</span>
                  </div>
                </div>
              </div>
            </div>

            <div className="flex gap-3">
              <Button
                onClick={() => navigate('/hotels')}
                variant="outline"
                className="flex-1"
              >
                <Icon name="ArrowLeft" size={16} className="mr-2" />
                Back to Hotels
              </Button>
              <Button
                onClick={() => navigate('/user-dashboard')}
                className="flex-1"
              >
                <Icon name="User" size={16} className="mr-2" />
                My Bookings
              </Button>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-card rounded-xl max-w-4xl w-full max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-border">
          <div>
            <h1 className="text-2xl font-bold text-foreground">Book {hotel.name}</h1>
            <p className="text-muted-foreground text-sm mt-1">
              Step {currentStep} of {steps.length}: {steps.find(s => s.id === currentStep)?.title}
            </p>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={onClose}
          >
            <Icon name="X" size={20} />
          </Button>
        </div>

        {/* Progress Bar */}
        <div className="px-6 py-4 bg-muted/30">
          <div className="flex items-center justify-between mb-2">
            {steps.map((step, index) => (
              <div key={step.id} className="flex items-center">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                  step.id <= currentStep
                    ? 'bg-primary text-primary-foreground'
                    : 'bg-muted text-muted-foreground'
                }`}>
                  {step.id < currentStep ? (
                    <Icon name="Check" size={14} />
                  ) : (
                    step.id
                  )}
                </div>
                {index < steps.length - 1 && (
                  <div className={`w-12 h-0.5 mx-2 ${
                    step.id < currentStep ? 'bg-primary' : 'bg-muted'
                  }`} />
                )}
              </div>
            ))}
          </div>
          <div className="text-xs text-muted-foreground text-center">
            {steps.find(s => s.id === currentStep)?.title}
          </div>
        </div>

        {/* Content */}
        <div className="p-6 max-h-[50vh] overflow-y-auto">
          {renderStepContent()}
        </div>

        {/* Footer */}
        {currentStep < 5 && (
          <div className="flex items-center justify-between p-6 border-t border-border bg-muted/30">
            <Button
              variant="outline"
              onClick={currentStep === 1 ? onClose : handlePrev}
              disabled={currentStep === 1}
            >
              <Icon name="ArrowLeft" size={16} className="mr-2" />
              {currentStep === 1 ? 'Cancel' : 'Previous'}
            </Button>

            <div className="flex items-center gap-4">
              {currentStep > 1 && currentStep < 4 && (
                <div className="text-sm text-muted-foreground">
                  Total: ${calculateTotal().total}
                </div>
              )}
              <Button
                onClick={handleNext}
                disabled={!validateStep(currentStep)}
              >
                {currentStep === 4 ? 'Complete Booking' : 'Next'}
                <Icon name="ArrowRight" size={16} className="ml-2" />
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default HotelBookingForm;